import { Injectable, inject } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Observable } from 'rxjs'
import { FuseqHeaders } from '../../FuseQ/fuseq.headers'
import { environment } from '../../../environments/environment'

/** Tipo di variazione rilevata tra due esecuzioni del monitoraggio. */
export type SitiAnagraficaChangeType = 'INSERT' | 'UPDATE' | 'DELETE'

export interface SitiAnagraficaChange {
  /** Identificatore stabile della singola variazione (checklist). */
  changeId: number
  idAnagrafica: string
  frazOld: string | null
  frazNew: string | null
  denominazioneOld: string | null
  denominazioneNew: string | null
  changeDate: string
  /** NULL = variazione ancora da completare, valorizzato = fatta a mano. */
  doneAt: string | null
  changeType: SitiAnagraficaChangeType
  changedFields: string
}

export interface SitiAnagraficaChangesPage {
  rows: SitiAnagraficaChange[]
  total: number
}

export interface SitiAnagraficaChangesSummary {
  totalChanges: number
  pendingChanges: number
  doneChanges: number
  changesLast30d: number
  sitesChanged: number
  lastChangeAt: string | null
  monitoredSites: number
  lastRunAt: string | null
}

export interface SitiAnagraficaChangesQuery {
  q?: string
  field?: '' | 'fraz' | 'denominazione'
  type?: '' | SitiAnagraficaChangeType
  status?: '' | 'pending' | 'done'
  days?: number
  limit?: number
  offset?: number
}

export interface SitiAnagraficaChangeDoneResult {
  changeId: number
  doneAt: string | null
}

@Injectable({
  providedIn: 'root'
})
export class AwpSitiAnagraficaChangesApiService {
  private readonly http = inject(HttpClient)
  private readonly fuseqHeaders = inject(FuseqHeaders)

  private base (): string {
    return `${environment.API_URL}/sitiAnagraficaChanges`
  }

  getSummary (): Observable<SitiAnagraficaChangesSummary> {
    return this.http.get<SitiAnagraficaChangesSummary>(`${this.base()}/summary`, this.fuseqHeaders.setTokenHeaders('GET'))
  }

  getChanges (query: SitiAnagraficaChangesQuery = {}): Observable<SitiAnagraficaChangesPage> {
    let params = new HttpParams()
    if (query.q) params = params.set('q', query.q)
    if (query.field) params = params.set('field', query.field)
    if (query.type) params = params.set('type', query.type)
    if (query.status) params = params.set('status', query.status)
    if (query.days) params = params.set('days', String(query.days))
    if (query.limit != null) params = params.set('limit', String(query.limit))
    if (query.offset != null) params = params.set('offset', String(query.offset))
    return this.http.get<SitiAnagraficaChangesPage>(`${this.base()}/changes`, {
      ...this.fuseqHeaders.setTokenHeaders('GET'),
      params
    })
  }

  /** Segna (o riapre) una singola variazione. */
  setDone (changeId: number, done = true): Observable<SitiAnagraficaChangeDoneResult> {
    return this.http.post<SitiAnagraficaChangeDoneResult>(
      `${this.base()}/changes/${changeId}/done`,
      { done },
      this.fuseqHeaders.setTokenHeaders('POST')
    )
  }

  /** Segna (o riapre) piu' variazioni in un colpo solo. */
  setDoneBulk (ids: number[], done = true): Observable<{ updated: number }> {
    return this.http.post<{ updated: number }>(
      `${this.base()}/changes/done`,
      { ids, done },
      this.fuseqHeaders.setTokenHeaders('POST')
    )
  }
}
